import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePurchase } from '../context/PurchaseContext';

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { confirmPurchase } = usePurchase();
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;
  const pollInterval = 2000;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/payments/status/${sessionId}`);

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();

      if (data.payment_status === 'paid' || data.has_access) {
        setStatus('success');
        confirmPurchase(new Date().toISOString());
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      
      if (attempts >= maxAttempts - 1) {
        setStatus('error');
      } else {
        setTimeout(pollPaymentStatus, pollInterval);
      }
    }
  };

  if (status === 'checking') {
    return (
      <div className="max-w-2xl mx-auto text-center fade-in">
        <div className="bg-white rounded-3xl border-2 border-stone-200 p-8 md:p-12">
          <div className="w-20 h-20 bg-primary/10 rounded-full mx-auto mb-6 flex items-center justify-center">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
          </div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-text mb-4">
            Processing Your Payment
          </h1>
          <p className="text-text-muted text-lg mb-4">
            Please wait while we confirm your purchase...
          </p>
          <div className="flex justify-center gap-1">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 bg-primary rounded-full animate-bounce"
                style={{ animationDelay: `${i * 0.1}s` }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="max-w-2xl mx-auto text-center fade-in">
        <div className="bg-white rounded-3xl border-2 border-error/30 p-8 md:p-12">
          <div className="w-20 h-20 bg-error/10 rounded-full mx-auto mb-6 flex items-center justify-center">
            <XCircle className="w-10 h-10 text-error" />
          </div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-text mb-4">
            Payment Verification Issue
          </h1>
          <p className="text-text-muted text-lg mb-8">
            We couldn't verify your payment. If you were charged, please contact support. 
            Your access will be activated shortly.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={() => navigate('/purchase')}
              variant="outline"
              className="rounded-full px-6 py-4 font-semibold"
            >
              Try Again
            </Button>
            <Button
              onClick={() => navigate('/')}
              className="bg-primary text-white hover:bg-primary-600 rounded-full px-6 py-4 font-semibold"
            >
              Go to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="max-w-2xl mx-auto text-center fade-in">
      <div className="bg-white rounded-3xl border-2 border-success/30 p-8 md:p-12">
        <div className="w-20 h-20 bg-success/10 rounded-full mx-auto mb-6 flex items-center justify-center">
          <CheckCircle className="w-10 h-10 text-success" />
        </div>
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-text mb-4">
          🎉 Payment Successful!
        </h1>
        <p className="text-text-muted text-lg mb-8">
          Thank you for your purchase! You now have full access to all 12 modules 
          of the Digital Safety Course.
        </p>
        <Button
          onClick={() => navigate('/module/introduction')}
          data-testid="start-learning"
          className="bg-primary text-white hover:bg-primary-600 rounded-full px-8 py-6 text-lg font-bold"
        >
          Start Learning Now
        </Button>
        <p className="text-text-muted text-sm mt-6">
          A confirmation email has been sent to your email address.
        </p>
      </div>
    </div>
  );
}
